import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { teacherPriorityApi, type PrepResponse } from '@/services/api/teacherPriority'
import { PrepAttachmentResponse, LessonPrepStatusResponse } from '@/types/teacher-request'
import { PrepFileUpload } from './PrepFileUpload'
import { sortLessonsByDateTime } from '@/utils/lessonSort'

interface PrepEditorProps {
  requestId: string
  preps: PrepResponse[]
  lessonStatus: LessonPrepStatusResponse[]
  selectedPrepId: string | null
  onPrepChange: (prepId: string | null) => void
  onSaved: () => void
  isReadOnly?: boolean
}

export function PrepEditor({ requestId, preps, lessonStatus, selectedPrepId, onPrepChange, onSaved, isReadOnly }: PrepEditorProps) {
  const queryClient = useQueryClient()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [durationMinutes, setDurationMinutes] = useState<number | undefined>()
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isNew, setIsNew] = useState(false)
  const [pendingLessonIds, setPendingLessonIds] = useState<string[]>([])
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [isAccepting, setIsAccepting] = useState(false)

  const selectedPrep = preps.find(p => p.id === selectedPrepId)

  const invalidateAcceptanceQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['preps', requestId] })
    queryClient.invalidateQueries({ queryKey: ['lesson-status', requestId] })
    queryClient.invalidateQueries({ queryKey: ['request-status', requestId] })
    queryClient.invalidateQueries({ queryKey: ['prep-requests', requestId] })
  }

  const handleAccept = async () => {
    if (!selectedPrepId || isAccepting) return
    setIsAccepting(true)
    setError(null)
    try {
      await teacherPriorityApi.acceptPrep(requestId, selectedPrepId)
      invalidateAcceptanceQueries()
    } catch (e: any) {
      setError(e?.detail || 'Prep konnte nicht akzeptiert werden')
    } finally {
      setIsAccepting(false)
    }
  }

  const { data: attachments = [] } = useQuery<PrepAttachmentResponse[]>({
    queryKey: ['prep-attachments', requestId, selectedPrepId],
    queryFn: () => selectedPrepId ? teacherPriorityApi.getPrepAttachments(requestId, selectedPrepId) : Promise.resolve([]),
    enabled: !!selectedPrepId,
  })

  useEffect(() => {
    if (selectedPrep) {
      setTitle(selectedPrep.title || '')
      setDescription(selectedPrep.description || '')
      setDurationMinutes(selectedPrep.duration_minutes || undefined)
      setNotes(selectedPrep.notes || '')
      setIsNew(false)
      setPendingLessonIds([])
      setPendingFiles([])
    } else if (isNew) {
      setTitle('')
      setDescription('')
      setDurationMinutes(undefined)
      setNotes('')
      setPendingLessonIds([])
      setPendingFiles([])
    }
  }, [selectedPrep, isNew])

  const handleNew = () => {
    onPrepChange(null)
    setIsNew(true)
    setTitle('')
    setDescription('')
    setDurationMinutes(undefined)
    setNotes('')
    setPendingLessonIds([])
    setPendingFiles([])
  }

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Bitte geben Sie einen Titel ein')
      return
    }
    setIsSubmitting(true)
    setError(null)
    try {
      if (selectedPrepId && !isNew) {
        await teacherPriorityApi.updatePrep(requestId, selectedPrepId, {
          title: title.trim(),
          description: description.trim() || undefined,
          duration_minutes: durationMinutes ?? undefined,
          notes: notes.trim() || undefined,
        })
      } else {
        const validPendingIds = pendingLessonIds.filter(id => lessonStatus.some(l => l.lesson_request_id === id))
        const result = await teacherPriorityApi.createPrep(requestId, {
          title: title.trim(),
          description: description.trim() || undefined,
          duration_minutes: durationMinutes ?? undefined,
          notes: notes.trim() || undefined,
          lesson_request_ids: validPendingIds.length > 0 ? validPendingIds : undefined,
        })
        onPrepChange(result.id)
        setIsNew(false)
        setPendingLessonIds([])
        
        if (pendingFiles.length > 0) {
          for (const file of pendingFiles) {
            try {
              await teacherPriorityApi.uploadPrepAttachment(requestId, result.id, file)
            } catch (e: any) {
              setError(`Datei "${file.name}" konnte nicht hochgeladen werden`)
              break
            }
          }
          setPendingFiles([])
          queryClient.invalidateQueries({ queryKey: ['prep-attachments', requestId, result.id] })
        }
      }
      onSaved()
      queryClient.invalidateQueries({ queryKey: ['preps', requestId] })
      queryClient.invalidateQueries({ queryKey: ['lesson-status', requestId] })
    } catch (e: any) {
      setError(e?.detail || 'Prep konnte nicht gespeichert werden')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedPrepId || !confirm('Prep wirklich löschen?')) return
    try {
      await teacherPriorityApi.deletePrep(requestId, selectedPrepId)
      onPrepChange(null)
      onSaved()
      queryClient.invalidateQueries({ queryKey: ['preps', requestId] })
      queryClient.invalidateQueries({ queryKey: ['lesson-status', requestId] })
    } catch (e: any) {
      setError(e?.detail || 'Prep konnte nicht gelöscht werden')
    }
  }

  const handleLinkLesson = async (lessonId: string) => {
    if (isNew) {
      setPendingLessonIds(prev => [...prev, lessonId])
      return
    }
    
    if (!selectedPrepId) return
    
    try {
      await teacherPriorityApi.createPrepLink(requestId, selectedPrepId, { lesson_request_id: lessonId })
      queryClient.invalidateQueries({ queryKey: ['preps', requestId] })
      queryClient.invalidateQueries({ queryKey: ['lesson-status', requestId] })
    } catch (e: any) {
      setError(e?.detail || 'Verknüpfung fehlgeschlagen')
    }
  }

  const handleUnlinkLesson = async (linkId: string) => {
    if (!selectedPrepId) return
    try {
      await teacherPriorityApi.deletePrepLink(requestId, selectedPrepId, linkId)
      queryClient.invalidateQueries({ queryKey: ['preps', requestId] })
      queryClient.invalidateQueries({ queryKey: ['lesson-status', requestId] })
    } catch (e: any) {
      setError(e?.detail || 'Verknüpfung konnte nicht entfernt werden')
    }
  }

  const handleUnlinkPendingLesson = (lessonId: string) => {
    setPendingLessonIds(prev => prev.filter(id => id !== lessonId))
  }

  const linkedLessonIds = selectedPrep?.linked_lessons?.map(l => l.lesson_request_id) || []
  const pendingOrLinkedIds = [...linkedLessonIds, ...pendingLessonIds]
  const unlinkedLessons = sortLessonsByDateTime(lessonStatus.filter(l => !pendingOrLinkedIds.includes(l.lesson_request_id)))

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-y-auto h-full">
      <div className="p-4 border-b bg-gray-50 rounded-t-xl flex justify-between items-center">
        <div>
          <h3 className="text-sm font-semibold text-gray-700">Prep bearbeiten</h3>
          <p className="text-xs text-gray-400 mt-0.5">{selectedPrep ? selectedPrep.title : isNew ? 'Neues Prep' : 'Wählen Sie ein Prep'}</p>
        </div>
        <div className="flex items-center gap-2">
          {selectedPrep && (selectedPrep.total_affected_teachers ?? 0) > 0 && (
            <>
              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                selectedPrep.fully_accepted
                  ? 'bg-green-100 text-green-700'
                  : 'bg-blue-50 text-blue-600'
              }`}>
                {selectedPrep.acceptance_count ?? 0}/{selectedPrep.total_affected_teachers} akzeptiert
              </span>
              {selectedPrep.current_teacher_pending_acceptance && (
                <button
                  onClick={handleAccept}
                  disabled={isAccepting}
                  className="px-3 py-1.5 text-xs bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
                >
                  {isAccepting ? '...' : 'Prep akzeptieren'}
                </button>
              )}
              {selectedPrep.accepted_by_current_teacher && !selectedPrep.current_teacher_pending_acceptance && (
                <span className="text-xs text-green-600 font-medium px-2 py-1">Akzeptiert ✓</span>
              )}
            </>
          )}
          {!isReadOnly && (
            <button onClick={handleNew} className="text-xs bg-blue-500 text-white px-3 py-1.5 rounded-lg hover:bg-blue-600">
              + Neues Prep
            </button>
          )}
        </div>
      </div>

      {(selectedPrepId || isNew) && (
        <div className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Verknüpfte Lektionen</label>
            <div className="flex flex-wrap gap-1.5">
              {sortLessonsByDateTime(selectedPrep?.linked_lessons || []).map(link => (
                <span key={link.id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                  {link.time_slot_name} ({link.date})
                  {!isReadOnly && (
                    <button aria-label="Lektion entfernen" onClick={() => handleUnlinkLesson(link.id)} className="text-blue-400 hover:text-blue-600">×</button>
                  )}
                </span>
              ))}
              {sortLessonsByDateTime(pendingLessonIds.map(id => lessonStatus.find(l => l.lesson_request_id === id)).filter((l): l is LessonPrepStatusResponse => l !== undefined)).map(lesson => (
                <span key={`pending-${lesson.lesson_request_id}`} className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-50 border border-dashed border-yellow-300 rounded-lg text-xs text-yellow-700">
                  {lesson.time_slot_name} ({lesson.date})
                  {!isReadOnly && (
                    <button aria-label="Lektion entfernen" onClick={() => handleUnlinkPendingLesson(lesson.lesson_request_id)} className="text-yellow-400 hover:text-yellow-600">×</button>
                  )}
                </span>
              ))}
              {!isReadOnly && unlinkedLessons.length > 0 && (
                <select
                  onChange={e => { if (e.target.value) handleLinkLesson(e.target.value); e.target.value = '' }}
                  className="px-2 py-1 border border-dashed border-gray-300 rounded-lg text-xs text-gray-400"
                  value=""
                  disabled={isSubmitting}
                >
                  <option value="">+ Lektion hinzufügen</option>
                  {sortLessonsByDateTime(unlinkedLessons).map(l => (
                    <option key={l.lesson_request_id} value={l.lesson_request_id}>{l.time_slot_name} ({l.date})</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Titel *</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="z.B. Arbeitsblätter kopieren"
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              maxLength={200}
              disabled={isReadOnly}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Beschreibung</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Details zur Vorbereitung..."
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              rows={4}
              disabled={isReadOnly}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Dauer (Minuten)</label>
              <input
                type="number"
                value={durationMinutes ?? ''}
                onChange={e => setDurationMinutes(e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="z.B. 15"
                className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                min={0}
                disabled={isReadOnly}
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Notizen (intern)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Zusätzliche Notizen..."
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
              rows={2}
              disabled={isReadOnly}
            />
          </div>

          {(selectedPrepId || isNew) && (
            <PrepFileUpload
              requestId={requestId}
              prepId={selectedPrepId}
              attachments={attachments}
              pendingFiles={pendingFiles}
              onPendingFilesChange={setPendingFiles}
              onUploadComplete={() => queryClient.invalidateQueries({ queryKey: ['prep-attachments', requestId, selectedPrepId] })}
              isReadOnly={isReadOnly}
            />
          )}

          {!isReadOnly && (
            <div className="flex justify-between pt-2">
              {selectedPrepId && !isNew ? (
                <button onClick={handleDelete} className="px-4 py-2 text-sm text-red-500 hover:text-red-700">Löschen</button>
              ) : <div />}
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    onPrepChange(null)
                    setIsNew(false)
                    setPendingLessonIds([])
                    setPendingFiles([])
                  }}
                  className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                >
                  {isSubmitting ? 'Wird gespeichert...' : 'Speichern'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {!selectedPrepId && !isNew && preps.length > 0 && (
        <div className="p-4">
          <p className="text-sm text-gray-500 mb-3">Wählen Sie ein bestehendes Prep aus oder erstellen Sie ein neues.</p>
          <div className="space-y-2">
            {preps.map(prep => (
              <button
                key={prep.id}
                onClick={() => { onPrepChange(prep.id); setIsNew(false) }}
                className="w-full text-left p-3 bg-gray-50 border rounded-lg hover:bg-gray-100 transition"
              >
                <div className="text-sm font-semibold text-gray-800">{prep.title}</div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {prep.linked_lessons?.length || 0} Lektionen · {prep.attachment_count || 0} Dateien
                  {prep.duration_minutes && ` · ${prep.duration_minutes} Min.`}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {!selectedPrepId && !isNew && preps.length === 0 && (
        <div className="p-4 text-center text-sm text-gray-500">
          Noch keine Preps vorhanden. Klicken Sie auf "+ Neues Prep" um zu beginnen.
        </div>
      )}
    </div>
  )
}
